import { useAtomValue, useSetAtom } from 'jotai';

import { Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import {
	FiltersDrawerCheckboxGroup,
	type FiltersDrawerCheckboxGroupItem,
} from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import {
	modelFilterAtom,
	typeFilterAtom,
	updateModelFilterAtom,
	updateTypeFilterAtom,
} from '../../atoms/ordersAtoms';
import { useFilterFacetCounts } from '../../hooks/useFilterFacetCounts';

/**
 * FiltersDrawerVehicleInformationSection component that provides vehicle information
 * filtering options wrapped in an accordion
 */
export function FiltersDrawerVehicleInformationSection() {
	const models = useAtomValue(modelFilterAtom);
	const types = useAtomValue(typeFilterAtom);

	const setModels = useSetAtom(updateModelFilterAtom);
	const setTypes = useSetAtom(updateTypeFilterAtom);

	// Get dynamic facet counts from the search response
	const { modelCounts, typeCounts } = useFilterFacetCounts();

	// Build model items with dynamic counts from facets
	// Sort alphabetically for consistent ordering
	const modelItems: FiltersDrawerCheckboxGroupItem[] = Object.entries(modelCounts)
		.map(([model, count]) => ({
			value: model,
			label: model,
			count,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	// Build type items with dynamic counts from facets
	// Sort by count (descending) then alphabetically for consistent ordering
	const typeItems: FiltersDrawerCheckboxGroupItem[] = Object.entries(typeCounts)
		.map(([type, count]) => ({
			value: type,
			label: type,
			count,
		}))
		.sort((a, b) => {
			// First sort by count (descending)
			if (b.count !== a.count) {
				return b.count - a.count;
			}
			// Then sort alphabetically
			return a.label.localeCompare(b.label);
		});

	return (
		<AccordionSection title='Vehicle information'>
			<Stack itemsSpacing='24'>
				<FiltersDrawerCheckboxGroup
					label='Model'
					items={modelItems}
					value={models}
					onChange={setModels}
				/>
				<FiltersDrawerCheckboxGroup
					label='Type'
					items={typeItems}
					value={types}
					onChange={setTypes}
				/>
			</Stack>
		</AccordionSection>
	);
}
